import type { ProviderSideCardProps } from "../../core/types/tsx/components/Profesional/ProviderSideCard.types";
import { Clock, MapPin, CheckCircle2, ShieldCheck, SprayCan } from "lucide-react";

const DAY_ORDER: Record<string, number> = {
	Lunes: 1,
	Martes: 2,
	Miercoles: 3,
	Jueves: 4,
	Viernes: 5,
	Sabado: 6,
	Domingo: 7,
};

const formatTo12h = (time?: string | null) => {
	if (!time) return "";

	const [rawHour, rawMinute] = time.split(":");
	const hour = Number(rawHour);
	const minute = Number(rawMinute ?? 0);

	if (!Number.isFinite(hour) || !Number.isFinite(minute)) return time;

	const period = hour >= 12 ? "PM" : "AM";
	const displayHour = hour % 12 || 12;
	const displayMinute = String(minute).padStart(2, "0");

	return `${displayHour}:${displayMinute} ${period}`;
};

export const ProviderSideCard = ({
	location,
	responseTime,
	priceFrom,
	category,
	categoryIcon,
	categories,
	onHire,
	schedules,
}: ProviderSideCardProps) => {
	const formatResponseTime = (minutes?: number) => {
		if (minutes === undefined || minutes === null || minutes <= 0) return "N/A";
		if (minutes < 60) return `${minutes} min`;
		const hours = Math.round(minutes / 60);
		return `${hours} ${hours === 1 ? "hora" : "horas"}`;
	};

	const activeSchedules = (schedules ?? [])
		.filter((s) => s.is_active)
		.sort(
			(a, b) => (DAY_ORDER[a.day_of_week] ?? 99) - (DAY_ORDER[b.day_of_week] ?? 99),
		);

	return (
		<div className='w-full max-w-none lg:max-w-sm bg-white rounded-3xl border border-gray-100 shadow-lg p-4 sm:p-6 font-sans'>
			<div className='flex flex-col'>
				{/* --- Botón de Acción (primero en mobile) --- */}
				<div className='order-1 mb-5 sm:order-2 sm:mb-8'>
					<button
						type='button'
						onClick={onHire}
						className='cursor-pointer w-full bg-primary hover:bg-primary-hover transition-colors text-white font-semibold py-3 px-4 rounded-full'
					>
						Contratar
					</button>
				</div>

				{/* --- Encabezado de Precio --- */}
				<div className='order-2 mb-5 sm:order-1 sm:mb-6'>
					<h2 className='text-2xl sm:text-3xl font-bold text-primary'>
						Desde {priceFrom || "$300"}
					</h2>
					<p className='text-slate-500 text-sm mt-1'>
						Precio inicial por servicio
					</p>
				</div>
			</div>

			<hr className='border-gray-100 mb-5 sm:mb-6' />

			{/* --- Información Clave --- */}
			<div className='space-y-5 sm:space-y-6 mb-5 sm:mb-6'>
				{/* Tiempo */}
				<div className='flex gap-4 items-start'>
					<Clock className='text-primary mt-1' size={20} />
					<div>
						<h3 className='text-gray-600 font-medium'>Tiempo de Respuesta</h3>
						<p className='text-gray-400 text-sm'>
							{formatResponseTime(responseTime)}
						</p>
					</div>
				</div>

				{/* Ubicación */}
				<div className='flex gap-4 items-start'>
					<MapPin className='text-primary mt-1' size={20} />
					<div>
						<h3 className='text-gray-600 font-medium'>Ubicación</h3>
						<p className='text-gray-400 text-sm'>
							{location || "Ubicación no disponible"}
						</p>
					</div>
				</div>

				{/* Verificado */}
				<div className='flex gap-4 items-start'>
					<CheckCircle2 className='text-primary mt-1' size={20} />
					<div>
						<h3 className='text-gray-600 font-medium'>Verificado</h3>
						<p className='text-gray-400 text-sm'>Identidad confirmada</p>
					</div>
				</div>
			</div>

			<hr className='border-gray-100 mb-5 sm:mb-6' />

			{/* --- Disponibilidad --- */}
			<div className='mb-6'>
				<h3 className='text-gray-500 font-medium mb-3'>Disponibilidad</h3>
				<div className='space-y-2'>
					{activeSchedules.length > 0 ? (
						activeSchedules.map((schedule) => (
							<div
								key={`${schedule.day_of_week}-${schedule.start_time}`}
								className='flex w-full flex-col rounded-2xl border border-border px-4 py-2 text-center text-sm font-medium text-text-placeholder sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:rounded-full'
							>
								<span>{schedule.day_of_week}</span>
								<span>
									{formatTo12h(schedule.start_time)} -{" "}
									{formatTo12h(schedule.end_time)}
								</span>
							</div>
						))
					) : (
						<p className='text-gray-400 text-sm'>Sin horario definido</p>
					)}
				</div>
			</div>

			<hr className='border-gray-100 mb-5 sm:mb-6' />

			{/* --- Garantías --- */}
			<div className='mb-6'>
				<h3 className='text-gray-500 font-medium mb-3'>Garantías</h3>
				<ul className='space-y-2'>
					{[
						"100% Satisfacción Garantizada",
						"Seguro de Responsabilidad Civil",
						"Productos de Alta Calidad",
					].map((item, index) => (
						<li
							key={index}
							className='flex items-center gap-2 text-sm text-gray-600'
						>
							<ShieldCheck className='text-sky-400' size={16} />
							{item}
						</li>
					))}
				</ul>
			</div>

			<hr className='border-gray-100 mb-6' />

			{/* --- Categoría --- */}
			<div>
				<h3 className='text-gray-500 font-medium mb-3'>Categoría</h3>
				<div className='flex flex-wrap gap-2'>
					{Array.isArray(categories) && categories.length > 0 ? (
						categories.map((categoryItem, idx) => (
							<span
								key={categoryItem.name || idx}
								className='inline-flex items-center gap-2 bg-secondary text-primary px-4 py-1.5 rounded-full text-sm font-medium max-w-full'
							>
								{categoryItem.icon ? (
									<img
										src={categoryItem.icon}
										alt={`Icono de ${categoryItem.name || "categoría"}`}
										className='w-4 h-4 object-contain'
									/>
								) : (
									<SprayCan size={16} />
								)}
								<span className='truncate'>
									{categoryItem.name || "General"}
								</span>
							</span>
						))
					) : Array.isArray(category) ? (
						category.map((cat, idx) => (
							<span
								key={cat || idx}
								className='inline-flex items-center gap-2 bg-secondary text-primary px-4 py-1.5 rounded-full text-sm font-medium max-w-full'
							>
								{categoryIcon && idx === 0 ? (
									<img
										src={categoryIcon}
										alt={`Icono de ${cat || "categoría"}`}
										className='w-4 h-4 object-contain'
									/>
								) : idx === 0 ? (
									<SprayCan size={16} />
								) : null}
								<span className='truncate'>{cat || "General"}</span>
							</span>
						))
					) : (
						<span className='inline-flex items-center gap-2 bg-secondary text-primary px-4 py-1.5 rounded-full text-sm font-medium max-w-full'>
							{categoryIcon ? (
								<img
									src={categoryIcon}
									alt={`Icono de ${category || "categoría"}`}
									className='w-4 h-4 object-contain'
								/>
							) : (
								<SprayCan size={16} />
							)}
							<span className='truncate'>{category || "General"}</span>
						</span>
					)}
				</div>
			</div>
		</div>
	);
};
